import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { staffAPI } from '../services/api';
import { UserPlus, RefreshCw, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const defaultSchedule = {
  monday: { working: true, start: '07:00', end: '15:30' },
  tuesday: { working: true, start: '07:00', end: '15:30' },
  wednesday: { working: true, start: '07:00', end: '15:30' },
  thursday: { working: true, start: '07:00', end: '15:30' },
  friday: { working: true, start: '07:00', end: '15:30' },
  saturday: { working: false, start: null, end: null },
  sunday: { working: false, start: null, end: null }
};

const StaffManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', schedule: { ...defaultSchedule } });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPin, setShowPin] = useState({});

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const data = await staffAPI.getAll();
      setStaffList(data);
    } catch (err) {
      setError('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleCreate = async () => {
    try {
      setError('');
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('First name and last name are required');
        return;
      }
      const result = await staffAPI.create(formData.firstName, formData.lastName, formData.email, formData.phone, formData.schedule);
      setSuccess(`Staff member created. PIN: ${result.pin_display}`);
      setShowModal(false);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', schedule: { ...defaultSchedule } });
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create staff member');
    }
  };

  const handleUpdate = async () => {
    try {
      setError('');
      await staffAPI.update(editingStaff.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        schedule: formData.schedule
      });
      setSuccess('Staff member updated');
      setShowModal(false);
      setEditingStaff(null);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', schedule: { ...defaultSchedule } });
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update staff member');
    }
  };

  const handleRegeneratePin = async (id) => {
    try {
      const result = await staffAPI.regeneratePin(id);
      setSuccess(`New PIN for ${result.first_name}: ${result.pin_display}`);
      fetchStaff();
    } catch (err) {
      setError('Failed to regenerate PIN');
    }
  };

  const handleToggleActive = async (staff) => {
    try {
      if (staff.is_active) {
        await staffAPI.deactivate(staff.id);
      } else {
        await staffAPI.activate(staff.id);
      }
      fetchStaff();
    } catch (err) {
      setError('Failed to update staff status');
    }
  };

  const openEditModal = (staff) => {
    setEditingStaff(staff);
    setFormData({
      firstName: staff.first_name,
      lastName: staff.last_name,
      email: staff.email || '',
      phone: staff.phone || '',
      schedule: staff.schedule || { ...defaultSchedule }
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingStaff(null);
    setFormData({ firstName: '', lastName: '', email: '', phone: '', schedule: { ...defaultSchedule } });
    setShowModal(true);
  };

  const updateScheduleDay = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: field === 'working' ? value : value,
          ...(field === 'working' && !value ? { start: null, end: null } : {}),
          ...(field === 'working' && value ? { start: '07:00', end: '15:30' } : {})
        }
      }
    }));
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Dashboard</button>
              <button onClick={() => navigate('/pallets')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Pallet Sheets</button>
              <button onClick={() => navigate('/reports')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Reports</button>
              {user?.role === 'admin' && (
                <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#10b981', cursor: 'pointer', fontWeight: '600' }}>Admin</button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }} style={{ padding: '8px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Logout</button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '28px', color: '#111827' }}>Staff Management</h2>
            <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>Manage warehouse staff, PINs, and schedules</p>
          </div>
          <button
            onClick={openCreateModal}
            style={{
              padding: '12px 24px', backgroundColor: '#10b981', color: 'white', border: 'none',
              borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '500',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            <UserPlus size={18} /> Add Staff
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {error}
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '18px' }}>x</button>
          </div>
        )}
        {success && (
          <div style={{ padding: '12px 16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#16a34a', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {success}
            <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: '18px' }}>x</button>
          </div>
        )}

        {/* Staff List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading staff...</div>
        ) : staffList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <p style={{ color: '#6b7280', fontSize: '16px' }}>No staff members yet. Click "Add Staff" to get started.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {staffList.map(staff => (
              <div key={staff.id} style={{
                backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: staff.is_active ? '2px solid transparent' : '2px solid #fecaca',
                opacity: staff.is_active ? 1 : 0.7
              }}>
                {/* Staff row */}
                <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '50%',
                      backgroundColor: staff.is_active ? '#d1fae5' : '#fee2e2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '18px', fontWeight: '600', color: staff.is_active ? '#065f46' : '#991b1b'
                    }}>
                      {staff.first_name[0]}{staff.last_name[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#111827' }}>
                        {staff.first_name} {staff.last_name}
                        {!staff.is_active && <span style={{ color: '#ef4444', fontSize: '12px', marginLeft: '8px' }}>(Inactive)</span>}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>PIN: {showPin[staff.id] ? staff.pin_display : '****'}</span>
                        <button
                          onClick={() => setShowPin(prev => ({ ...prev, [staff.id]: !prev[staff.id] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#6b7280' }}
                        >
                          {showPin[staff.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        {staff.phone && <span style={{ marginLeft: '4px' }}>{staff.phone}</span>}
                        {staff.email && <span style={{ marginLeft: '4px' }}>{staff.email}</span>}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => handleRegeneratePin(staff.id)}
                      style={{
                        padding: '8px 12px', backgroundColor: '#f0f9ff', color: '#0369a1',
                        border: '1px solid #bae6fd', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px'
                      }}
                    >
                      <RefreshCw size={14} /> New PIN
                    </button>
                    <button
                      onClick={() => openEditModal(staff)}
                      style={{
                        padding: '8px 16px', backgroundColor: '#f5f3ff', color: '#7c3aed',
                        border: '1px solid #ddd6fe', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(staff)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: staff.is_active ? '#fef2f2' : '#f0fdf4',
                        color: staff.is_active ? '#dc2626' : '#16a34a',
                        border: `1px solid ${staff.is_active ? '#fecaca' : '#bbf7d0'}`,
                        borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
                      }}
                    >
                      {staff.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => setExpandedStaff(expandedStaff === staff.id ? null : staff.id)}
                      style={{
                        padding: '8px', backgroundColor: '#f3f4f6', color: '#374151',
                        border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer'
                      }}
                    >
                      {expandedStaff === staff.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded schedule view */}
                {expandedStaff === staff.id && (
                  <div style={{ padding: '0 24px 16px', borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ paddingTop: '12px' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Weekly Schedule</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
                        {DAYS.map(day => {
                          const daySchedule = staff.schedule?.[day] || { working: false };
                          return (
                            <div key={day} style={{
                              padding: '8px', borderRadius: '8px', textAlign: 'center',
                              backgroundColor: daySchedule.working ? '#f0fdf4' : '#f9fafb',
                              border: `1px solid ${daySchedule.working ? '#bbf7d0' : '#e5e7eb'}`
                            }}>
                              <div style={{ fontWeight: '600', fontSize: '13px', color: daySchedule.working ? '#16a34a' : '#9ca3af', marginBottom: '4px' }}>
                                {DAY_LABELS[day]}
                              </div>
                              {daySchedule.working ? (
                                <div style={{ fontSize: '12px', color: '#374151' }}>
                                  {daySchedule.start} - {daySchedule.end}
                                </div>
                              ) : (
                                <div style={{ fontSize: '12px', color: '#9ca3af' }}>Off</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', color: '#111827' }}>
              {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
            </h3>

            {/* Name fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                    borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                    borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Contact fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Email (optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="staff@example.com"
                  style={{
                    width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                    borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="04XX XXX XXX"
                  style={{
                    width: '100%', padding: '10px 12px', border: '2px solid #d1d5db',
                    borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Schedule */}
            <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#374151' }}>Weekly Schedule</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
              {DAYS.map(day => {
                const daySchedule = formData.schedule[day] || { working: false, start: null, end: null };
                return (
                  <div key={day} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px',
                    backgroundColor: '#f9fafb', borderRadius: '8px'
                  }}>
                    <div style={{ width: '40px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                      {DAY_LABELS[day]}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={daySchedule.working}
                        onChange={e => updateScheduleDay(day, 'working', e.target.checked)}
                        style={{ width: '18px', height: '18px', accentColor: '#10b981' }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>Working</span>
                    </label>
                    {daySchedule.working && (
                      <>
                        <input
                          type="time"
                          value={daySchedule.start || '07:00'}
                          onChange={e => updateScheduleDay(day, 'start', e.target.value)}
                          style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <span style={{ color: '#6b7280' }}>to</span>
                        <input
                          type="time"
                          value={daySchedule.end || '15:30'}
                          onChange={e => updateScheduleDay(day, 'end', e.target.value)}
                          style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => { setShowModal(false); setEditingStaff(null); setError(''); }}
                style={{
                  padding: '10px 24px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '15px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={editingStaff ? handleUpdate : handleCreate}
                style={{
                  padding: '10px 24px', backgroundColor: '#10b981', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '500'
                }}
              >
                {editingStaff ? 'Save Changes' : 'Create Staff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
